'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function OhmsLawCalculator() {
  const t = useTranslations("tools.ohmsLawCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    voltage: '',
    current: '',
    resistance: '',
    power: ''
  });

  const [units, setUnits] = useState({
    voltage: 'V',
    current: 'A',
    resistance: 'Ω',
    power: 'W'
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const convertToBase = (value: number, unit: string, type: string): number => {
    const conversions: any = {
      voltage: { 'kV': 1000, 'V': 1, 'mV': 0.001 },
      current: { 'A': 1, 'mA': 0.001 },
      resistance: { 'Ω': 1, 'kΩ': 1000, 'MΩ': 1000000 },
      power: { 'W': 1, 'kW': 1000, 'MW': 1000000, 'HP': 745.7, 'BTU/h': 0.293071 }
    };
    return value * conversions[type][unit];
  };

  const formatResult = (value: number, unit: string): string => {
    let displayValue = value;
    let displayUnit = unit;

    if (Math.abs(value) >= 1000000) {
      displayValue = value / 1000000;
      displayUnit = unit === 'Ω' ? 'MΩ' : unit === 'A' ? 'MA' : unit === 'V' ? 'MV' : 'MW';
    } else if (Math.abs(value) >= 1000) {
      displayValue = value / 1000;
      displayUnit = unit === 'Ω' ? 'kΩ' : unit === 'A' ? 'kA' : unit === 'V' ? 'kV' : 'kW';
    } else if (Math.abs(value) < 1 && Math.abs(value) > 0) {
      displayValue = value * 1000;
      displayUnit = unit === 'A' ? 'mA' : unit === 'V' ? 'mV' : unit;
    }

    return displayValue.toFixed(4).replace(/\.?0+$/, '') + ' ' + displayUnit;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const values = {
      voltage: inputs.voltage ? convertToBase(parseFloat(inputs.voltage), units.voltage, 'voltage') : null,
      current: inputs.current ? convertToBase(parseFloat(inputs.current), units.current, 'current') : null,
      resistance: inputs.resistance ? convertToBase(parseFloat(inputs.resistance), units.resistance, 'resistance') : null,
      power: inputs.power ? convertToBase(parseFloat(inputs.power), units.power, 'power') : null
    };

    const filledCount = Object.values(values).filter(v => v !== null && !isNaN(v as number)).length;

    if (filledCount < 2) {
      setError(t('errorMinimum'));
      return;
    }

    if (filledCount > 2) {
      setError(t('errorMaximum'));
      return;
    }

    let V = values.voltage;
    let I = values.current;
    let R = values.resistance;
    let P = values.power;

    if (V !== null && I !== null) {
      R = V / I;
      P = V * I;
    } else if (V !== null && R !== null) {
      I = V / R;
      P = (V * V) / R;
    } else if (V !== null && P !== null) {
      I = P / V;
      R = (V * V) / P;
    } else if (I !== null && R !== null) {
      V = I * R;
      P = I * I * R;
    } else if (I !== null && P !== null) {
      V = P / I;
      R = P / (I * I);
    } else if (R !== null && P !== null) {
      I = Math.sqrt(P / R);
      V = Math.sqrt(P * R);
    }

    if (V! <= 0 || I! <= 0 || R! <= 0 || P! <= 0) {
      setError(t('errorInvalid'));
      return;
    }

    setResults({
      voltage: formatResult(V!, 'V'),
      current: formatResult(I!, 'A'),
      resistance: formatResult(R!, 'Ω'),
      power: formatResult(P!, 'W')
    });
  };

  const handleClear = () => {
    setInputs({ voltage: '', current: '', resistance: '', power: '' });
    setUnits({ voltage: 'V', current: 'A', resistance: 'Ω', power: 'W' });
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueLight: '#3b82f6',
    purple: '#7c3aed',
    yellow: '#f59e0b',
    green: '#10b981',
    red: '#ef4444',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    },
    { padding: '2rem 1rem' },
    { padding: '2.5rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '1000px', margin: '0 auto' },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const toolTitleStyle = getResponsiveStyle(
    { color: colors.textPrimary, marginBottom: '0.5rem', fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginTop: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle = {
    display: 'flex',
    gap: '0.5rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      fontFamily: 'inherit'
    },
    { padding: '0.625rem', fontSize: '0.95rem' },
    { padding: '0.7rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      minWidth: '100px',
      fontFamily: 'inherit'
    },
    { padding: '0.625rem', fontSize: '0.95rem' },
    { padding: '0.7rem', fontSize: '1rem', minWidth: '110px' },
    { padding: '0.75rem', fontSize: '1rem', minWidth: '120px' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
      fontFamily: 'inherit'
    },
    { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
    { padding: '0.8rem 1.4rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      fontFamily: 'inherit'
    },
    { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
    { padding: '0.8rem 1.4rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const circuitDiagramStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const circuitSvgStyle = getResponsiveStyle(
    { width: '100%', height: 'auto' },
    { maxWidth: '250px' },
    { maxWidth: '280px' },
    { maxWidth: '300px' }
  );

  const formulaWheelStyle = getResponsiveStyle(
    {
      marginTop: '1.5rem',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      textAlign: 'center' as const
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const formulaWheelTitleStyle = getResponsiveStyle(
    { color: '#92400e', marginBottom: '1rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const formulasStyle = getResponsiveStyle(
    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', color: '#78350f' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginTop: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: `4px solid ${colors.blue}`
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: colors.textSecondary, marginBottom: '0.5rem', fontWeight: 500 },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: '4px solid #dc2626'
    },
    { padding: '0.75rem', fontSize: '0.875rem' },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolTitleStyle}>⚡ {t('toolTitle')}</h2>
            <p style={toolSubtitleStyle}>{t('toolSubtitle')}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorFormStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="voltage">{t('voltageLabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="voltage"
                      step="any"
                      placeholder={t('voltagePlaceholder')}
                      value={inputs.voltage}
                      onChange={(e) => setInputs({...inputs, voltage: e.target.value})}
                      style={inputStyle}
                    />
                    <select
                      id="voltage-unit"
                      value={units.voltage}
                      onChange={(e) => setUnits({...units, voltage: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="kV">kV</option>
                      <option value="V">V</option>
                      <option value="mV">mV</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="current">{t('currentLabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="current"
                      step="any"
                      placeholder={t('currentPlaceholder')}
                      value={inputs.current}
                      onChange={(e) => setInputs({...inputs, current: e.target.value})}
                      style={inputStyle}
                    />
                    <select
                      id="current-unit"
                      value={units.current}
                      onChange={(e) => setUnits({...units, current: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="A">A</option>
                      <option value="mA">mA</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="resistance">{t('resistanceLabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="resistance"
                      step="any"
                      placeholder={t('resistancePlaceholder')}
                      value={inputs.resistance}
                      onChange={(e) => setInputs({...inputs, resistance: e.target.value})}
                      style={inputStyle}
                    />
                    <select
                      id="resistance-unit"
                      value={units.resistance}
                      onChange={(e) => setUnits({...units, resistance: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="Ω">Ω</option>
                      <option value="kΩ">kΩ</option>
                      <option value="MΩ">MΩ</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="power">{t('powerLabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="power"
                      step="any"
                      placeholder={t('powerPlaceholder')}
                      value={inputs.power}
                      onChange={(e) => setInputs({...inputs, power: e.target.value})}
                      style={inputStyle}
                    />
                    <select
                      id="power-unit"
                      value={units.power}
                      onChange={(e) => setUnits({...units, power: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="W">W</option>
                      <option value="kW">kW</option>
                      <option value="MW">MW</option>
                      <option value="HP">HP</option>
                      <option value="BTU/h">BTU/h</option>
                    </select>
                  </div>
                </div>

                {error && (
                  <div style={errorMessageStyle}>
                    {error}
                  </div>
                )}

                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                  <button type="button" onClick={handleClear} style={clearBtnStyle}>{t('clearButton')}</button>
                </div>
              </form>
            </div>

            <div style={circuitDiagramStyle}>
              <svg style={circuitSvgStyle} viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
                <circle cx="150" cy="100" r="80" fill="none" stroke={colors.blue} strokeWidth="3"/>
                <circle cx="150" cy="40" r="15" fill={colors.purple}/>
                <text x="150" y="25" textAnchor="middle" fill={colors.textPrimary} fontWeight="bold" fontSize="14">V</text>
                <circle cx="230" cy="100" r="15" fill={colors.yellow}/>
                <text x="250" y="105" textAnchor="start" fill={colors.textPrimary} fontWeight="bold" fontSize="14">I</text>
                <circle cx="150" cy="160" r="15" fill={colors.green}/>
                <text x="150" y="185" textAnchor="middle" fill={colors.textPrimary} fontWeight="bold" fontSize="14">R</text>
                <circle cx="70" cy="100" r="15" fill={colors.red}/>
                <text x="50" y="105" textAnchor="end" fill={colors.textPrimary} fontWeight="bold" fontSize="14">P</text>
                <line x1="150" y1="55" x2="150" y2="20" stroke={colors.textSecondary} strokeWidth="2"/>
                <line x1="215" y1="100" x2="250" y2="100" stroke={colors.textSecondary} strokeWidth="2"/>
                <line x1="150" y1="145" x2="150" y2="180" stroke={colors.textSecondary} strokeWidth="2"/>
                <line x1="85" y1="100" x2="50" y2="100" stroke={colors.textSecondary} strokeWidth="2"/>
              </svg>

              <div style={formulaWheelStyle}>
                <h4 style={formulaWheelTitleStyle}>{t('formulaWheelTitle')}</h4>
                <div style={formulasStyle}>
                  <div>V = I × R</div>
                  <div>P = V × I</div>
                  <div>I = V / R</div>
                  <div>P = I² × R</div>
                  <div>R = V / I</div>
                  <div>P = V² / R</div>
                </div>
              </div>
            </div>
          </div>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>
              <div style={resultsGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('voltageResult')}</span>
                  <span style={resultValueStyle}>{results.voltage}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('currentResult')}</span>
                  <span style={resultValueStyle}>{results.current}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resistanceResult')}</span>
                  <span style={resultValueStyle}>{results.resistance}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('powerResult')}</span>
                  <span style={resultValueStyle}>{results.power}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice1Title')}</strong> {t('practice1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice2Title')}</strong> {t('practice2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice3Title')}</strong> {t('practice3Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice4Title')}</strong> {t('practice4Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('practice5Title')}</strong> {t('practice5Description')}</li>
          <li><strong style={strongStyle}>{t('practice6Title')}</strong> {t('practice6Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('coreFormulasTitle')}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>V = I × R:</strong> {t('formula1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>I = V / R:</strong> {t('formula2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>R = V / I:</strong> {t('formula3Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>P = V × I:</strong> {t('formula4Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>P = I² × R:</strong> {t('formula5Description')}</li>
          <li><strong style={strongStyle}>P = V² / R:</strong> {t('formula6Description')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.375rem' : '1.5rem', marginTop: '1.5rem'}}>{t('applicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('application1Title')}</strong> {t('application1Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('application2Title')}</strong> {t('application2Description')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong style={strongStyle}>{t('application3Title')}</strong> {t('application3Description')}</li>
          <li><strong style={strongStyle}>{t('application4Title')}</strong> {t('application4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem'}}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem'}}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem'}}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem'}}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem'}}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.keysight.com/used/us/en/knowledge/calculators/ohms-law-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.electricalsafetyfirst.org.uk/ohms-law-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.eimtechnology.com/blogs/articles/what-is-ohms-law-calculation" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li style={{marginBottom: '0.75rem'}}>
            <a href="https://www.build-electronic-circuits.com/ohms-law/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference4')}
            </a>
          </li>
          <li>
            <a href="https://www.electronics-tutorials.ws/dccircuits/dcp_2.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}